/**
 * **What every settings page is given** (W28, FR-65, plan D88): the service's
 * client, the engine's settings routes, the reader's permissions, the
 * organisation's name, the FiveOX console and the host's step-up — once, by
 * the `/settings` route, so the hub and each page read them from one place and
 * a test hands a fake in the same way (`SettingsHostProvider`).
 */
import type { ProDocClient, StepUpAsk, Verb } from '@fiveox/prodoc-ui'
import { createContext, useContext, type ReactNode } from 'react'
import { Outlet } from 'react-router'

import { useOrg } from '../../components/org-context'
import { useStepUp } from '../../components/StepUpDialog'
import { parsingApi, prodoc } from '../../lib/api'
import { CONSOLE_URL } from '../../lib/config'
import type { ParsingApi } from '../../lib/parsing'

export interface SettingsHost {
  client: ProDocClient
  /** The engine's two settings routes (C-E3): the org's profiles, and AI processing behind step-up. */
  parsing: ParsingApi
  can: (resource: string, verb: Verb) => boolean
  orgName: string
  /** The FiveOX console, where what is not ProDoc's is managed (FR-37). */
  consoleUrl: string
  /** The host's step-up, for an erasure request (D25) and for AI processing (engine D13); without it AI processing is not offered. */
  requestStepUp?: StepUpAsk
}

const SettingsHostContext = createContext<SettingsHost | null>(null)

export function SettingsHostProvider({ value, children }: { value: SettingsHost; children: ReactNode }) {
  return <SettingsHostContext.Provider value={value}>{children}</SettingsHostContext.Provider>
}

export function useSettingsHost(): SettingsHost {
  const host = useContext(SettingsHostContext)
  if (!host) throw new Error('A settings page is rendered inside <SettingsHostProvider> (the /settings route).')
  return host
}

/** The `/settings` route: the app's host for the hub and every page under it. */
export function SettingsRoute() {
  const { org, can } = useOrg()
  const stepUp = useStepUp()
  return (
    <SettingsHostProvider value={{ client: prodoc, parsing: parsingApi, can, orgName: org.display_name, consoleUrl: CONSOLE_URL, requestStepUp: stepUp }}>
      <Outlet />
    </SettingsHostProvider>
  )
}
